import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { asyncGetAllSurveyResponses } from '../../../../../services/survey.service';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { SurveyResponseListActions } from './actions';
import { SurveyResponseFilter } from '../filters/props';

interface SurveyResponse {
    id: string;
    survey: {
        id: string;
        code: string;
        title: string;
    };
    caller_id: string;
    document_number: string;
    customer_name: string;
    associated_service: string;
    status: string;
    created_at: string;
    updated_at: string;
}

export const useSurveyResponseList = (filters?: SurveyResponseFilter) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<SurveyResponse[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<SurveyResponseFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const fetchSurveyResponses = () => {
        setLoading(true);
        const searchParams: any = {
            page,
            limit: pageSize,
        };

        // Filtros específicos
        if (finalFilters.status) searchParams.status = finalFilters.status;
        if (finalFilters.surveyId)
            searchParams.surveyId = finalFilters.surveyId;
        if (finalFilters.surveyCode)
            searchParams.surveyCode = finalFilters.surveyCode;
        if (finalFilters.documentNumber)
            searchParams.document_number = finalFilters.documentNumber;
        if (finalFilters.customerName)
            searchParams.customer_name = finalFilters.customerName;
        if (finalFilters.associatedService)
            searchParams.associated_service = finalFilters.associatedService;
        if (finalFilters.startDate)
            searchParams.startDate = finalFilters.startDate;
        if (finalFilters.endDate) searchParams.endDate = finalFilters.endDate;

        asyncGetAllSurveyResponses(searchParams)
            .then((res: any) => {
                setRows(res.items || []);
                setTotal(res.length || 0);
            })
            .finally(() => setLoading(false));
    };

    const columns: GridColDef<SurveyResponse>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <SurveyResponseListActions
                    id={row.row.id}
                    onRefresh={fetchSurveyResponses}
                />
            ),
        },
        {
            field: 'survey_code',
            headerName: 'Código',
            width: 220,
            renderCell: (row) => (
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                    }}
                >
                    {row.row.survey?.code || ''}
                </div>
            ),
        },
        {
            field: 'survey',
            headerName: 'Encuesta',
            width: 250,
            flex: 1,
            renderCell: (row) => (
                <TableTooltip value={row.row.survey?.title || ''} />
            ),
        },
        {
            field: 'document_number',
            headerName: 'Número de Documento',
            width: 150,
        },
        {
            field: 'customer_name',
            headerName: 'Nombre Cliente',
            width: 150,
            renderCell: (row) => <TableTooltip value={row.row.customer_name} />,
        },
        {
            field: 'associated_service',
            headerName: 'Servicio Asociado',
            width: 150,
            renderCell: (row) => (
                <TableTooltip value={row.row.associated_service} />
            ),
        },
        {
            field: 'status',
            headerName: 'Estado',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <>
                    {row.row.status === 'completed'
                        ? 'Completada'
                        : 'Incompleta'}
                </>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        },
    ];

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        fetchSurveyResponses();
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
